import { useOrigin } from "@/features/flow/hooks/use-origin";
import { FromURL } from "@/features/flow/types";
import { formatUTC } from "@/features/utils/functions";
import { useNativeInputs } from "@/features/utils/is-mobile";
import _ from "lodash";
import { DateTime } from "luxon";
import type React from "react";
import { useState } from "react";
import "react-calendar/dist/Calendar.css";
import { twMerge } from "tailwind-merge";
import FormattedInputDate from "./FormattedInputDate.react";
import { defaultInputClassName } from "./Input.react";

type InputDateProps = {
  value: Date | null;
  onChange?: (date: Date | null) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  autoComplete?: string;
  labelProps?: React.ComponentPropsWithoutRef<"label">;
  dataCy?: string;
} & Omit<Omit<React.ComponentPropsWithoutRef<"input">, "onChange">, "value">;

function InputDate({
  value,
  placeholder,
  label,
  labelProps,
  autoComplete,
  onChange = () => {},
  dataCy,
  ...props
}: InputDateProps) {
  const isMobile = useNativeInputs();
  const [valueText, setValueText] = useState<string>(
    value
      ? formatUTC(new Date(value.getTime() - value.getTimezoneOffset() * 60000).toISOString())
      : ""
  );
  const [textColor, setTextColor] = useState<string>(
    value ? "text-black" : "text-slate-400 dark:text-slate-600"
  );
  const [missingRequired, setMissingRequired] = useState(false);
  const handleFocus = () => {
    if (!valueText) {
      setTextColor("text-black");
    }
  };
  const origin = useOrigin();
  const handleBlur = () => {
    const date = DateTime.fromISO(valueText);
    if (date.isValid) {
      setTextColor("text-black");
    } else {
      setMissingRequired(true);
      setTextColor("text-slate-400 dark:text-slate-600");
    }
  };

  const onInputChange = (newValue: string) => {
    setValueText(newValue);
    const date = DateTime.fromISO(newValue);
    if (date.isValid) {
      onChange(date.toJSDate());
    } else {
      onChange(null);
    }
    if (props?.required) {
      setMissingRequired(!date.isValid);
    } else {
      setMissingRequired(false);
    }
  };

  return (
    <label
      className={twMerge(
        "flex flex-col",
        props.disabled && "pointer-events-none pointer-events-none opacity-40",
        props.className
      )}
      {..._.omit(labelProps, "className")}
    >
      {label && (
        <span
          className={twMerge(
            "block whitespace-nowrap pb-1 text-left font-medium text-base text-black dark:text-white"
          )}
        >
          {label}
          {props.required === false ? "" : " *"}
        </span>
      )}
      <div>
        {isMobile && (
          <input
            autoComplete={autoComplete}
            type="date"
            max="2999-12-31"
            required
            pattern="\d{4}-\d{2}-\d{2}"
            data-cy={dataCy}
            value={valueText}
            onFocus={handleFocus}
            onBlur={handleBlur}
            className={twMerge(
              defaultInputClassName,
              origin !== FromURL.None ? "focus:border-primary dark:focus:border-primary" : "",

              "w-max",
              missingRequired && "border-red-500",
              textColor,
              props.className
            )}
            placeholder={placeholder}
            onChange={(e) => onInputChange(e.target.value)}
            {..._.omit(props, "onChange", "type", "value", "placeholder", "className")}
          />
        )}
        {!isMobile && (
          <FormattedInputDate
            value={valueText}
            dataCy={dataCy}
            className={twMerge(
              defaultInputClassName,
              "w-full",
              textColor,
              props.className,
              origin !== FromURL.None ? "focus:border-primary dark:focus:border-primary" : "",
              missingRequired && "border-red-500"
            )}
            onChange={onInputChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            missingRequired={missingRequired}
          />
        )}
      </div>
    </label>
  );
}

export default InputDate;
